// llm.js — one client for every provider the app can talk to.
//
// Before this, the same fetch-to-Anthropic block was copy-pasted across eight
// files, each with its own hardcoded model, headers, usage event, and error
// string. This consolidates them and lets a user bring an OpenAI, Gemini, or
// OpenAI-compatible key instead.
//
// ── What does NOT work everywhere ──────────────────────────────────────────
// Four features rely on Anthropic's server-side web tools (web_search /
// web_fetch), where the model retrieves a page itself and reasons over it:
// recipe search, and import-from-URL for recipes, appliances, and coffee bags.
// OpenAI and Gemini have no equivalent, so those functions require Anthropic.
// Rather than fail with a confusing provider error, callers check
// `providerSupports(provider, "web_tools")` first and tell the user plainly.
//
// The agentic chat (lib/chat.js) is also Anthropic-only, for a different
// reason: it hand-parses Anthropic's SSE event shapes and runs a 19-tool loop
// whose prompts are tuned to Claude. Porting it is a rewrite, not a config
// change, so it is out of scope here.

const DEFAULT_MAX_TOKENS = 4096;

// ── Provider registry ──────────────────────────────────────────────────────
// `models.default` / `models.cheap` mirror the split the app already used:
// heavier reasoning on the default, cheap classification on the small model.
export const PROVIDERS = {
  anthropic: {
    id: "anthropic",
    label: "Anthropic (Claude)",
    keyPlaceholder: "sk-ant-api03-...",
    keyUrl: "https://console.anthropic.com/settings/keys",
    capabilities: { text: true, vision: true, web_tools: true },
    models: { default: "claude-sonnet-4-6", cheap: "claude-haiku-4-5" },
  },
  openai: {
    id: "openai",
    label: "OpenAI (ChatGPT)",
    keyPlaceholder: "sk-proj-...",
    keyUrl: "https://platform.openai.com/api-keys",
    capabilities: { text: true, vision: true, web_tools: false },
    models: { default: "gpt-4o", cheap: "gpt-4o-mini" },
  },
  gemini: {
    id: "gemini",
    label: "Google Gemini",
    keyPlaceholder: "AIza...",
    keyUrl: "https://aistudio.google.com/app/apikey",
    capabilities: { text: true, vision: true, web_tools: false },
    models: { default: "gemini-2.0-flash", cheap: "gemini-2.0-flash-lite" },
  },
  compatible: {
    id: "compatible",
    label: "OpenAI-compatible endpoint",
    // One adapter covers OpenRouter, Groq, Together, and a local Ollama —
    // they all speak /v1/chat/completions. The user supplies base URL + model.
    keyPlaceholder: "sk-... (or any value your endpoint accepts)",
    keyUrl: null,
    capabilities: { text: true, vision: false, web_tools: false },
    models: { default: "", cheap: "" },
    needsBaseUrl: true,
  },
};

export const DEFAULT_PROVIDER = "anthropic";

// ── Ambient provider config ────────────────────────────────────────────────
// Call sites already thread an `apiKey` through ~95 places. Rather than widen
// every signature to carry provider + baseUrl as well, useStore publishes the
// active selection here once when preferences load, and callLLM falls back to
// it. Callers that want to override still can, by passing `provider` directly.
let active = { provider: DEFAULT_PROVIDER, baseUrl: null };

export function setActiveProvider({ provider, baseUrl } = {}) {
  active = {
    provider: PROVIDERS[provider] ? provider : DEFAULT_PROVIDER,
    baseUrl: baseUrl || null,
  };
}

export function getActiveProvider() {
  return { ...active };
}

export function providerInfo(provider) {
  return PROVIDERS[provider] || PROVIDERS[DEFAULT_PROVIDER];
}

/**
 * Is a feature available on the active provider? UI uses this to disable or
 * explain a control before the user clicks it, rather than failing after.
 */
export function activeSupports(capability) {
  return providerSupports(active.provider, capability);
}

/** Does this provider support a capability? ("text" | "vision" | "web_tools") */
export function providerSupports(provider, capability) {
  return !!providerInfo(provider).capabilities[capability];
}

/**
 * Thrown when a caller needs something the active provider cannot do. Carries
 * enough context for the UI to write a specific message rather than "error".
 */
export class UnsupportedByProviderError extends Error {
  constructor(provider, capability, feature) {
    const p = providerInfo(provider);
    super(
      `${feature} needs Anthropic. ${p.label} can't ${
        capability === "web_tools" ? "fetch web pages" : capability
      }. Switch providers in Settings, or add the details manually.`
    );
    this.name = "UnsupportedByProviderError";
    this.provider = provider;
    this.capability = capability;
    this.feature = feature;
  }
}

/** Throw a clear error unless the provider supports `capability`. */
export function assertSupported(provider, capability, feature) {
  if (!providerSupports(provider, capability)) {
    throw new UnsupportedByProviderError(provider, capability, feature);
  }
}

// ── Usage telemetry ────────────────────────────────────────────────────────
// Every call site used to emit this event itself with a hardcoded model string.
// Normalizing here keeps Settings' usage reporting working across providers,
// which each name their token counts differently.
function emitUsage(kind, provider, model, usage) {
  if (!usage || typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent("cucina-ai-usage", {
      detail: { kind, provider, model, usage },
    }));
  } catch {}
}

function normalizeUsage(provider, raw) {
  if (!raw) return null;
  if (provider === "gemini") {
    return {
      input_tokens: raw.promptTokenCount ?? 0,
      output_tokens: raw.candidatesTokenCount ?? 0,
    };
  }
  if (provider === "anthropic") {
    return { input_tokens: raw.input_tokens ?? 0, output_tokens: raw.output_tokens ?? 0 };
  }
  // OpenAI and compatible endpoints
  return { input_tokens: raw.prompt_tokens ?? 0, output_tokens: raw.completion_tokens ?? 0 };
}

// ── Adapters ───────────────────────────────────────────────────────────────
// Each takes the normalized request and returns { text, toolUses, usage }.
//
// Normalized request shape:
//   system    string
//   messages  [{ role: "user"|"assistant", content: string }]
//   images    [{ mediaType, data }]  base64, attached to the last user message
//   tools     Anthropic tool defs (only meaningful for the anthropic adapter)

async function callAnthropic({ apiKey, model, system, messages, images, tools, maxTokens }) {
  const msgs = messages.map(m => ({ role: m.role, content: m.content }));
  if (images?.length) {
    const last = msgs[msgs.length - 1];
    last.content = [
      ...images.map(img => ({
        type: "image",
        source: { type: "base64", media_type: img.mediaType, data: img.data },
      })),
      { type: "text", text: typeof last.content === "string" ? last.content : "" },
    ];
  }

  const body = { model, max_tokens: maxTokens, system, messages: msgs };
  if (tools) body.tools = tools;

  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      "anthropic-version": "2023-06-01",
      // Required for browser-side calls; do not remove.
      "anthropic-dangerous-direct-browser-access": "true",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Anthropic ${res.status}: ${(await res.text()).slice(0, 200)}`);

  const json = await res.json();
  return {
    text: (json.content || []).filter(b => b.type === "text").map(b => b.text).join("\n"),
    toolUses: (json.content || []).filter(b => b.type === "tool_use"),
    usage: json.usage,
    raw: json,
  };
}

async function callOpenAICompatible({ apiKey, model, system, messages, images, maxTokens, baseUrl }) {
  const msgs = [];
  // OpenAI has no top-level system field — it is the first message.
  if (system) msgs.push({ role: "system", content: system });
  for (const m of messages) msgs.push({ role: m.role, content: m.content });

  if (images?.length) {
    const last = msgs[msgs.length - 1];
    // Vision uses data URIs rather than Anthropic's separate base64 block.
    last.content = [
      { type: "text", text: typeof last.content === "string" ? last.content : "" },
      ...images.map(img => ({
        type: "image_url",
        image_url: { url: `data:${img.mediaType};base64,${img.data}` },
      })),
    ];
  }

  const root = (baseUrl || "https://api.openai.com/v1").replace(/\/+$/, "");
  const res = await fetch(`${root}/chat/completions`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
    body: JSON.stringify({ model, messages: msgs, max_tokens: maxTokens }),
  });
  if (!res.ok) throw new Error(`OpenAI ${res.status}: ${(await res.text()).slice(0, 200)}`);

  const json = await res.json();
  return {
    text: json.choices?.[0]?.message?.content || "",
    toolUses: [],
    usage: json.usage,
    raw: json,
  };
}

async function callGemini({ apiKey, model, system, messages, images, maxTokens }) {
  // Gemini uses contents/parts, and "model" where the others say "assistant".
  const contents = messages.map(m => ({
    role: m.role === "assistant" ? "model" : "user",
    parts: [{ text: typeof m.content === "string" ? m.content : "" }],
  }));
  if (images?.length) {
    contents[contents.length - 1].parts.push(
      ...images.map(img => ({ inline_data: { mime_type: img.mediaType, data: img.data } }))
    );
  }

  const body = { contents, generationConfig: { maxOutputTokens: maxTokens } };
  if (system) body.systemInstruction = { parts: [{ text: system }] };

  const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${encodeURIComponent(apiKey)}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Gemini ${res.status}: ${(await res.text()).slice(0, 200)}`);

  const json = await res.json();
  return {
    text: (json.candidates?.[0]?.content?.parts || []).map(p => p.text || "").join(""),
    toolUses: [],
    usage: json.usageMetadata,
    raw: json,
  };
}

const ADAPTERS = {
  anthropic: callAnthropic,
  openai: callOpenAICompatible,
  compatible: callOpenAICompatible,
  gemini: callGemini,
};

/**
 * Call the user's configured provider.
 *
 *   provider   "anthropic" | "openai" | "gemini" | "compatible"
 *   apiKey     the user's key for that provider
 *   baseUrl    required for "compatible"
 *   model      explicit model, else tier picks from the registry
 *   tier       "default" | "cheap"  (ignored when `model` is given)
 *   system     system prompt
 *   messages   [{ role, content }]
 *   images     [{ mediaType, data }] base64 — attached to the last user message
 *   tools      Anthropic tool defs; ignored by other providers
 *   maxTokens  output cap
 *   kind       label for the usage event
 *
 * Returns { text, toolUses, usage, provider, model }.
 */
export async function callLLM({
  provider = active.provider,
  apiKey,
  baseUrl = active.baseUrl,
  model,
  tier = "default",
  system,
  messages,
  images,
  tools,
  maxTokens = DEFAULT_MAX_TOKENS,
  kind = "llm",
  // Human-readable name of the feature being invoked, used in capability
  // errors so the user reads "Recipe import from a link needs Anthropic"
  // rather than a generic failure.
  feature = "This feature",
}) {
  const info = providerInfo(provider);
  if (!apiKey) throw new Error(`Add your ${info.label} API key in Settings.`);
  if (info.needsBaseUrl && !baseUrl) {
    throw new Error(`${info.label} needs a base URL — set it in Settings.`);
  }
  if (images?.length) assertSupported(provider, "vision", feature);
  if (tools?.length) assertSupported(provider, "web_tools", feature);

  const resolvedModel = model || info.models[tier] || info.models.default;
  if (!resolvedModel) throw new Error(`Set a model name for ${info.label} in Settings.`);

  const adapter = ADAPTERS[provider] || ADAPTERS[DEFAULT_PROVIDER];
  const out = await adapter({
    apiKey, model: resolvedModel, system,
    messages: messages || [], images, tools, maxTokens, baseUrl,
  });

  const usage = normalizeUsage(provider, out.usage);
  emitUsage(kind, provider, resolvedModel, usage);
  return { ...out, usage, provider, model: resolvedModel };
}
